//
//A cache for loading and saving images in elements ready to be drawn by the canvas.
//

import { useState, useRef } from "react";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { BackdropStyle, MapLabelStyle } from "./poster-project";
import { PointStyle } from "./poster-types";

interface ImageElememntCacheProps {
  isPublic: boolean;
  projectID: string;
}

export const useImageElementCache = (props: ImageElememntCacheProps) => {
  const { getImageUrl } = useImageStore();
  const [imageCache, setImageCache] = useState<Map<string, HTMLImageElement>>(
    new Map()
  );
  // Track failed image IDs to prevent infinite retry loops
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(
    new Set()
  );
  // Track in-flight requests to prevent duplicate fetches during concurrent renders
  // Uses a ref for synchronous updates (state updates are batched/async)
  const inFlightRequests = useRef<Set<string>>(new Set());
  // Ref mirrors of state for synchronous checks in async functions (avoids stale closures)
  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(imageCache);
  const failedImageIdsRef = useRef<Set<string>>(failedImageIds);
  // Keep refs in sync with state
  imageCacheRef.current = imageCache;
  failedImageIdsRef.current = failedImageIds;

  const cacheImageForBackdropStyle = async (backdropStyle: BackdropStyle) => {
    if (!backdropStyle.imageName) {
      return;
    }
    const cacheKey = backdropStyle.imageName;
    // Check if already cached, previously failed, or in-flight (using refs for fresh values)
    if (
      imageCacheRef.current.has(cacheKey) ||
      failedImageIdsRef.current.has(cacheKey) ||
      inFlightRequests.current.has(cacheKey)
    ) {
      return;
    }
    inFlightRequests.current.add(cacheKey);

    try {
      const url = await getImageUrl(
        backdropStyle.imageName,
        "jpeg",
        props.isPublic,
        props.isPublic
          ? "backdrops"
          : "projects/" + props.projectID + "/backdrops"
      );
      if (url) {
        await loadAndCacheImage(backdropStyle.imageName, url);
      } else {
        setFailedImageIds((prev) => new Set(prev).add(cacheKey));
      }
    } finally {
      inFlightRequests.current.delete(cacheKey);
    }
  };

  const cacheImageForMapLabelStyle = async (labelStyle: MapLabelStyle) => {
    if (!labelStyle.imageName) {
      return;
    }
    const cacheKey = labelStyle.imageName;
    // Check if already cached, previously failed, or in-flight (using refs for fresh values)
    if (
      imageCacheRef.current.has(cacheKey) ||
      failedImageIdsRef.current.has(cacheKey) ||
      inFlightRequests.current.has(cacheKey)
    ) {
      return;
    }
    inFlightRequests.current.add(cacheKey);

    try {
      const url = await getImageUrl(
        labelStyle.imageName,
        "jpeg",
        props.isPublic,
        props.isPublic ? "labels" : "projects/" + props.projectID + "/labels"
      );
      if (url) {
        await loadAndCacheImage(labelStyle.imageName, url);
      } else {
        setFailedImageIds((prev) => new Set(prev).add(cacheKey));
      }
    } finally {
      inFlightRequests.current.delete(cacheKey);
    }
  };

  const cacheImageForPointStyle = async (pointStyle: PointStyle) => {
    if (!pointStyle.imageName) {
      return;
    }
    const cacheKey = pointStyle.imageName;
    // Check if already cached, previously failed, or in-flight (using refs for fresh values)
    if (
      imageCacheRef.current.has(cacheKey) ||
      failedImageIdsRef.current.has(cacheKey) ||
      inFlightRequests.current.has(cacheKey)
    ) {
      return;
    }
    inFlightRequests.current.add(cacheKey);

    try {
      const url = await getImageUrl(
        pointStyle.imageName,
        "png",
        props.isPublic,
        props.isPublic
          ? "pointIcons"
          : "projects/" + props.projectID + "/pointIcons"
      );
      if (url) {
        await loadAndCacheImage(pointStyle.imageName, url);
      } else {
        setFailedImageIds((prev) => new Set(prev).add(cacheKey));
      }
    } finally {
      inFlightRequests.current.delete(cacheKey);
    }
  };

  const cacheImageForGalleryViewport = async (imageID: string) => {
    if (!imageID) {
      return;
    }
    const cacheKey = `gallery-${imageID}`;
    // Check if already cached, previously failed, or in-flight (using refs for fresh values)
    if (
      imageCacheRef.current.has(cacheKey) ||
      failedImageIdsRef.current.has(cacheKey) ||
      inFlightRequests.current.has(cacheKey)
    ) {
      return;
    }
    inFlightRequests.current.add(cacheKey);

    try {
      const url = await getImageUrl(
        imageID,
        "jpeg",
        props.isPublic,
        props.isPublic
          ? "gallery"
          : "projects/" + props.projectID + "/gallery"
      );
      if (url) {
        // Prefix with "gallery-" to namespace in cache
        await loadAndCacheImage(cacheKey, url);
      } else {
        // Mark as failed if URL couldn't be retrieved
        setFailedImageIds((prev) => new Set(prev).add(cacheKey));
      }
    } finally {
      // Always remove from in-flight tracking when done
      inFlightRequests.current.delete(cacheKey);
    }
  };

  const loadAndCacheImage = async (id: string, url: string) => {
    // Check if already cached or previously failed (using refs for fresh values)
    // Note: inFlightRequests is managed by the caller, not here
    if (
      imageCacheRef.current.has(id) ||
      failedImageIdsRef.current.has(id)
    ) {
      return;
    }

    const img = new window.Image();
    img.crossOrigin = "anonymous";
    let usingFallback = false;
    let loadFailed = false;

    await new Promise((resolve) => {
      img.onload = () => resolve(null);
      img.onerror = () => {
        console.warn(`Image load failed for ${url}, switching to fallback.`);
        usingFallback = true;
        loadFailed = true;
        img.src = "/icons/UI-ICONS/CONTRAST/UI-ICON-REMOVE.png";
        img.onload = () => resolve(null);
        img.onerror = () => resolve(null);
      };
      img.src = url;
    });

    try {
      await img.decode();
    } catch (err) {
      console.warn(
        `Image decode failed for ${
          usingFallback ? "fallback" : "original"
        } image:`,
        err
      );
    }

    // If original load failed, track it to prevent retrying
    if (loadFailed) {
      setFailedImageIds((prev) => new Set(prev).add(id));
    }

    setImageCache((prevCache) => {
      const newCache = new Map(prevCache);
      newCache.set(id, img);
      return newCache;
    });
  };

  return {
    imageCache,
    cacheImage: loadAndCacheImage,
    cacheImageForBackdropStyle,
    cacheImageForMapLabelStyle,
    cacheImageForPointStyle,
    cacheImageForGalleryViewport,
  };
};
